"use client";

import { c as _c } from "react/compiler-runtime";
import { cn } from "@workspace/ui/lib/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { createInitialState, DRONE_HEIGHT, DRONE_WIDTH, DRONE_X, flap, type GameState, isAnimating, step, WORLD_HEIGHT, WORLD_WIDTH } from "@/lib/flappy-drone/engine";
import { render } from "./draw";

/**
 * A small monochrome minigame for the moments a page has nothing better to
 * offer — a 404, a crash, a maintenance window. Fly the drone through the
 * scaffolding; press space, or tap.
 *
 * At rest it is only a drone, sitting where a page would otherwise put an
 * icon. The first press widens the panel and pulls the camera back until the
 * whole site is in view, so the game grows out of the graphic rather than
 * replacing it.
 *
 * The rules live in `lib/flappy-drone/engine.ts` and the pixels in `draw.ts`;
 * this component is only the bridge: size the canvas, take input, and run a
 * fixed-step loop while there is something to animate. When the scene settles
 * (at rest, or a wreck on the ground) the loop stops entirely, so an error
 * page nobody is playing with costs one frame.
 */

const BEST_SCORE_KEY = "magnus:flappy-drone:best";

/** Fixed simulation step, so physics do not depend on the display's refresh. */
const STEP_SECONDS = 1 / 120;
/** A backgrounded tab hands back a huge delta; never simulate more than this. */
const MAX_FRAME_SECONDS = 0.25;

/**
 * How long a crash is left to register before a press means "again". Without
 * it, anyone still mashing space at the moment they die restarts before they
 * have seen that they died. Measured in wall clock, since the game's own
 * clock stops with the wreck.
 */
const RESTART_DELAY_MS = 500;

/** Softens both ends of the strip so the world runs off rather than stops. */
const EDGE_FADE = "linear-gradient(to right, transparent 0%, black 10%, black 90%, transparent 100%)";

/** The panel at rest: wide enough for the drone and little else. */
const COLLAPSED_WIDTH = 116;
/** World units across the panel at rest — the drone is 24 of them. */
const COLLAPSED_SPAN = 66;
/** How long the panel takes to open out into the full strip. */
const REVEAL_MS = 480;
function lerp(from: number, to: number, t: number): number {
  return from + (to - from) * t;
}

/** Fast off the mark, settling at the end — the view arrives, then stops. */
function easeOut(t: number): number {
  return 1 - (1 - t) ** 3;
}
function readBestScore(): number {
  try {
    const raw = window.localStorage.getItem(BEST_SCORE_KEY);
    const parsed = raw ? Number.parseInt(raw, 10) : 0;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  } catch {
    return 0;
  }
}
function writeBestScore(score: number): void {
  try {
    window.localStorage.setItem(BEST_SCORE_KEY, String(score));
  } catch {
    // Private mode or a blocked origin — a lost high score is not worth a throw.
  }
}

/** Whether a key press belongs to something else on the page. */
function isInteractiveTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) {
    return false;
  }
  return Boolean(target.closest('button, a, input, textarea, select, [contenteditable="true"]'));
}

/** Keys that fly the drone from anywhere on the page. */
const FLAP_KEYS = new Set([" ", "Spacebar", "ArrowUp"]);
export function FlappyDrone(t0) {
  const $ = _c(32);
  const {
    className
  } = t0;
  const frameWidthRef = useRef(null);
  const panelRef = useRef(null);
  const canvasRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = createInitialState();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const stateRef = useRef(t1);
  const frameRef = useRef(0);
  const lastTimeRef = useRef(0);
  const carryRef = useRef(0);
  const bestRef = useRef(0);
  const reducedMotionRef = useRef(false);
  const crashedAtRef = useRef(0);
  const fullWidthRef = useRef(WORLD_WIDTH);
  const revealRef = useRef(0);
  const revealFromRef = useRef(0);
  const colorRef = useRef("currentColor");
  const [best, setBest] = useState(0);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      phase: "idle",
      score: 0
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [hud, setHud] = useState(t2);
  const [coarsePointer, setCoarsePointer] = useState(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => revealFromRef.current > 0 && revealRef.current < 1;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const isOpening = t3;
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const panel = panelRef.current;
      if (!panel) {
        return;
      }
      const width = lerp(COLLAPSED_WIDTH, fullWidthRef.current, revealRef.current);
      panel.style.width = `${width}px`;
      panel.style.height = `${width * WORLD_HEIGHT / WORLD_WIDTH}px`;
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const sizePanel = t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");
      if (!(canvas && ctx)) {
        return;
      }
      const reveal = revealRef.current;
      const zoom = WORLD_WIDTH / lerp(COLLAPSED_SPAN, WORLD_WIDTH, reveal);
      const shown = lerp(COLLAPSED_WIDTH, fullWidthRef.current, reveal);
      render(ctx, stateRef.current, {
        color: colorRef.current,
        best: bestRef.current,
        reducedMotion: reducedMotionRef.current,
        reveal,
        camera: {
          zoom,
          centerX: lerp(DRONE_X + DRONE_WIDTH / 2, WORLD_WIDTH / 2, reveal),
          centerY: lerp(stateRef.current.drone.y + DRONE_HEIGHT / 2, WORLD_HEIGHT / 2, reveal),
          hairline: WORLD_WIDTH / (shown * zoom)
        }
      });
    };
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const paint = t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      const {
        phase,
        score
      } = stateRef.current;
      setHud(current => current.phase === phase && current.score === score ? current : {
        phase,
        score
      });
    };
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const syncHud = t6;
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      if (frameRef.current) {
        return;
      }
      lastTimeRef.current = performance.now();
      const tick = now => {
        frameRef.current = 0;
        const wasPlaying = stateRef.current.phase === "playing";
        const delta = Math.min((now - lastTimeRef.current) / 1000, MAX_FRAME_SECONDS);
        lastTimeRef.current = now;
        carryRef.current = carryRef.current + delta;
        while (carryRef.current >= STEP_SECONDS) {
          stateRef.current = step(stateRef.current, STEP_SECONDS);
          carryRef.current = carryRef.current - STEP_SECONDS;
        }
        if (isOpening()) {
          const t = (now - revealFromRef.current) / REVEAL_MS;
          revealRef.current = t >= 1 ? 1 : easeOut(t);
          sizePanel();
        }
        paint();
        syncHud();
        if (wasPlaying && stateRef.current.phase === "over") {
          crashedAtRef.current = now;
          const {
            score: score_0
          } = stateRef.current;
          if (score_0 > bestRef.current) {
            bestRef.current = score_0;
            setBest(score_0);
            writeBestScore(score_0);
          }
        }
        if (isAnimating(stateRef.current) || isOpening()) {
          frameRef.current = requestAnimationFrame(tick);
        }
      };
      frameRef.current = requestAnimationFrame(tick);
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const runLoop = t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      if (stateRef.current.phase === "over" && performance.now() - crashedAtRef.current < RESTART_DELAY_MS) {
        return;
      }
      const next = flap(stateRef.current);
      if (next === stateRef.current) {
        return;
      }
      stateRef.current = next;
      carryRef.current = 0;
      if (revealFromRef.current === 0) {
        revealFromRef.current = performance.now();
        if (reducedMotionRef.current) {
          revealRef.current = 1;
          sizePanel();
        }
      }
      syncHud();
      paint();
      runLoop();
    };
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const onFlap = t8;
  let t10;
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      const frame = frameWidthRef.current;
      const canvas_0 = canvasRef.current;
      if (!(frame && canvas_0)) {
        return;
      }
      const resize = () => {
        const width_0 = Math.min(frame.clientWidth || WORLD_WIDTH, WORLD_WIDTH);
        fullWidthRef.current = width_0;
        const ratio = window.devicePixelRatio || 1;
        canvas_0.width = Math.round(width_0 * ratio);
        canvas_0.height = Math.round(width_0 * WORLD_HEIGHT * ratio / WORLD_WIDTH);
        const ctx_0 = canvas_0.getContext("2d");
        const scale = width_0 / WORLD_WIDTH * ratio;
        ctx_0?.setTransform(scale, 0, 0, scale, 0, 0);
        colorRef.current = window.getComputedStyle(canvas_0).color;
        sizePanel();
        paint();
      };
      resize();
      const observer = new ResizeObserver(resize);
      observer.observe(frame);
      return () => observer.disconnect();
    };
    t10 = [paint, sizePanel];
    $[8] = t10;
    $[9] = t9;
  } else {
    t10 = $[8];
    t9 = $[9];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => {
      const stored = readBestScore();
      bestRef.current = stored;
      setBest(stored);
      const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
      const pointer = window.matchMedia("(pointer: coarse)");
      reducedMotionRef.current = motion.matches;
      setCoarsePointer(pointer.matches);
      const onMotionChange = event => {
        reducedMotionRef.current = event.matches;
      };
      const onPointerChange = event_0 => {
        setCoarsePointer(event_0.matches);
      };
      motion.addEventListener("change", onMotionChange);
      pointer.addEventListener("change", onPointerChange);
      stateRef.current = createInitialState(Math.floor(Math.random() * 2147483648) || 1);
      paint();
      return () => {
        motion.removeEventListener("change", onMotionChange);
        pointer.removeEventListener("change", onPointerChange);
      };
    };
    t12 = [paint];
    $[10] = t11;
    $[11] = t12;
  } else {
    t11 = $[10];
    t12 = $[11];
  }
  useEffect(t11, t12);
  let t13;
  let t14;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      const onKeyDown = event_1 => {
        if (!FLAP_KEYS.has(event_1.key)) {
          return;
        }
        if (event_1.metaKey || event_1.ctrlKey || event_1.altKey) {
          return;
        }
        if (isInteractiveTarget(event_1.target)) {
          return;
        }
        event_1.preventDefault();
        onFlap();
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    };
    t14 = [onFlap];
    $[12] = t13;
    $[13] = t14;
  } else {
    t13 = $[12];
    t14 = $[13];
  }
  useEffect(t13, t14);
  let t15;
  let t16;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = () => {
      const onVisibility = () => {
        if (document.hidden) {
          cancelAnimationFrame(frameRef.current);
          frameRef.current = 0;
          return;
        }
        if (isAnimating(stateRef.current) || isOpening()) {
          runLoop();
        }
      };
      document.addEventListener("visibilitychange", onVisibility);
      return () => {
        document.removeEventListener("visibilitychange", onVisibility);
        cancelAnimationFrame(frameRef.current);
        frameRef.current = 0;
      };
    };
    t16 = [isOpening, runLoop];
    $[14] = t15;
    $[15] = t16;
  } else {
    t15 = $[14];
    t16 = $[15];
  }
  useEffect(t15, t16);
  const action = coarsePointer ? "Tap" : "Press space";
  let t17;
  if ($[16] !== action || $[17] !== best || $[18] !== hud.phase || $[19] !== hud.score) {
    t17 = hud.phase === "idle" ? `${action} to fly` : hud.phase === "playing" ? `Score ${hud.score}` : `Score ${hud.score} · Best ${best} · ${action.toLowerCase()} to retry`;
    $[16] = action;
    $[17] = best;
    $[18] = hud.phase;
    $[19] = hud.score;
    $[20] = t17;
  } else {
    t17 = $[20];
  }
  const hint = t17;
  let t18;
  if ($[21] !== className) {
    t18 = cn("flex w-full max-w-[440px] flex-col items-center gap-2", className);
    $[21] = className;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  let t20;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = event_2 => {
      if (!(FLAP_KEYS.has(event_2.key) || event_2.key === "Enter")) {
        return;
      }
      event_2.preventDefault();
      onFlap();
    };
    t20 = event_3 => {
      event_3.preventDefault();
      onFlap();
    };
    $[23] = t19;
    $[24] = t20;
  } else {
    t19 = $[23];
    t20 = $[24];
  }
  let t21;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = {
      width: COLLAPSED_WIDTH
    };
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <button aria-label={"Flappy Drone \u2014 fly the drone through the scaffolding"} className="cursor-pointer bg-transparent p-0 text-foreground/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onKeyDown={t19} onPointerDown={t20} ref={panelRef} style={t21} type="button"><canvas className="block size-full" ref={canvasRef} style={{
        maskImage: EDGE_FADE,
        WebkitMaskImage: EDGE_FADE
      }} /></button>;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== hint) {
    t23 = <p className="text-center font-mono text-muted-foreground/70 text-xs">{hint}</p>;
    $[27] = hint;
    $[28] = t23;
  } else {
    t23 = $[28];
  }
  let t24;
  if ($[29] !== t18 || $[30] !== t23) {
    t24 = <div className={t18} ref={frameWidthRef}>{t22}{t23}</div>;
    $[29] = t18;
    $[30] = t23;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  return t24;
}